import React, { useId } from 'react';
import { Checkbox, Box } from '@amzn/awsui-components-console';
import styles from './ExactMatchToggle.module.scss';

interface ExactMatchToggleProps {
  readonly exactMatch: boolean;
  readonly onExactMatchChange: (exactMatch: boolean) => void;
}

/**
 * A case mismatch looks like "no results" rather than like a matching rule, so the
 * constraint is stated inline instead of in a tooltip.
 *
 * The copy is a parenthetical fragment that reads as a continuation of the
 * "Exact match" label ("Exact match (case sensitive)"), which is why it MUST stay
 * an inline sibling of the checkbox rather than move anywhere else on the row.
 *
 * Rendered as that inline sibling wired up with `ariaDescribedby` rather than
 * passed as the `Checkbox`'s `description` prop: the two are equivalent to a
 * screen reader, but Cloudscape renders `description` on its own line below the
 * label and this hint has to sit beside it.
 */
const EXACT_MATCH_HINT = '(case sensitive)';

/**
 * How the query text is matched. Sits on its own row directly BELOW the query
 * input, because it qualifies what was just typed there.
 *
 * Split out of the former `SearchTypeToggle`, which shared a row with the
 * source/translation radios (now `SearchModeSelector`).
 */
const ExactMatchToggle: React.FC<ExactMatchToggleProps> = ({
  exactMatch,
  onExactMatchChange,
}) => {
  // Per-instance: a module-level id would point both checkboxes at the first hint
  // if this row were ever rendered twice on a page.
  const exactMatchHintId = useId();

  return (
    <div className={styles.container} data-testid="exact-match-toggle">
      <Checkbox
        checked={exactMatch}
        onChange={({ detail }) => onExactMatchChange(detail.checked)}
        ariaDescribedby={exactMatchHintId}
      >
        Exact match
      </Checkbox>
      {/* `Box`'s own `id` prop is deprecated, so the id goes on an inner span. */}
      <Box variant="small" color="text-status-inactive">
        <span id={exactMatchHintId}>{EXACT_MATCH_HINT}</span>
      </Box>
    </div>
  );
};

export default ExactMatchToggle;
